import { Star, Target, Rocket } from 'lucide-react'

export interface CaseStudyData {
  slug: string
  title: string
  client: string
  product: string
  industry: string
  outcome: string
  headline: string
  summary: string
  region: string
  integrations: string[]
  metrics: Array<{ label: string; value: string }>
  keyMetrics: Array<{ icon: any; label: string; value: string }>
  timeline: Array<{ t: string; d: string }>
  quotes: Array<{ q: string; a: string }>
  context: string
  solution: string
  results: string[]
  goLiveDate: string
}

export const getCaseStudyData = (slug: string, t: (key: string) => string): CaseStudyData => {
  const caseStudies: Record<string, CaseStudyData> = {
    'oncology-referrals-referralcx': {
      slug: 'oncology-referrals-referralcx',
      title: t("useCaseFinder.useCases.oncologyReferrals.title"),
      client: t("useCaseFinder.useCases.oncologyReferrals.client"),
      product: t("useCaseFinder.products.referralcx"),
      industry: t("useCaseFinder.industries.hospitals"),
      outcome: t("useCaseFinder.useCases.oncologyReferrals.outcome"),
      headline: "Streamlined Oncology Referrals — How Leading US Cancer Center scaled ReferralCX with EceptionistCX",
      summary: "Cut referral processing time by 60% while improving care coordination across specialty departments.",
      region: "US",
      integrations: ["Epic", "Cerner", "FHIR"],
      metrics: [
        { label: "Wait Time", value: "−60%" },
        { label: "Admin Time", value: "−45%" },
        { label: "Throughput", value: "+35%" },
      ],
      keyMetrics: [
        { icon: Star, label: "Patient Satisfaction", value: "4.8/5" },
        { icon: Target, label: "Referral Completion", value: "+42%" },
        { icon: Rocket, label: "Time‑to‑Value", value: "< 60 days" },
      ],
      timeline: [
        { t: "Week 0–2", d: "Discovery & design" },
        { t: "Week 3–6", d: "Build & integrations" },
        { t: "Week 7–8", d: "UAT & training" },
        { t: "Week 9", d: "Go‑live" },
      ],
      quotes: [
        { q: "EceptionistCX cut our referral admin time in half.", a: "Program Director, Leading US Cancer Center" },
        { q: "The waitlist automation is a game changer for capacity.", a: "Operations Lead, Leading US Cancer Center" },
      ],
      context: "Leading US Cancer Center faced significant challenges with manual referral processes, leading to delays in patient care and administrative burden. The organization needed a solution to streamline oncology referrals and improve care coordination.",
      solution: "EceptionistCX deployed ReferralCX with AI‑assisted triage and digital referral workflows. Key elements included smart intake forms with role‑based permissions, two‑way status visibility for providers and partners, waitlist automation and capacity‑aware scheduling, and FHIR‑based integration with Epic/Cerner and secure data exchange.",
      results: [
        "Referral processing time reduced by 60% (median)",
        "No‑show rate down 22% with automated reminders",
        "Operational reporting consolidated across programs"
      ],
      goLiveDate: "March 2024 in 6 weeks"
    },
    'cart-patient-flow-cellcx': {
      slug: 'cart-patient-flow-cellcx',
      title: t("useCaseFinder.useCases.cartPatientFlow.title"),
      client: t("useCaseFinder.useCases.cartPatientFlow.client"),
      product: t("useCaseFinder.products.cellcx"),
      industry: t("useCaseFinder.industries.research"),
      outcome: t("useCaseFinder.useCases.cartPatientFlow.outcome"),
      headline: "Coordinating CAR-T Patient Flow — How Global Academic Medical Center optimized CellCX workflows",
      summary: "Improved scheduling efficiency for complex cell therapy services with coordinated patient flow management.",
      region: "Global",
      integrations: ["Epic", "LIMS", "FHIR", "Azure"],
      metrics: [
        { label: "Patient Flow", value: "+40%" },
        { label: "Lab Efficiency", value: "+28%" },
        { label: "Scheduling", value: "+52%" },
      ],
      keyMetrics: [
        { icon: Star, label: "Patient Satisfaction", value: "4.9/5" },
        { icon: Target, label: "Treatment Completion", value: "+38%" },
        { icon: Rocket, label: "Time‑to‑Value", value: "< 45 days" },
      ],
      timeline: [
        { t: "Week 0–3", d: "Discovery & design" },
        { t: "Week 4–8", d: "Build & integrations" },
        { t: "Week 9–10", d: "UAT & training" },
        { t: "Week 11", d: "Go‑live" },
      ],
      quotes: [
        { q: "CellCX transformed our CAR-T program coordination.", a: "Clinical Director, Global Academic Medical Center" },
        { q: "The chain of custody tracking gives us complete confidence.", a: "Lab Manager, Global Academic Medical Center" },
      ],
      context: "Global Academic Medical Center needed to coordinate complex CAR-T patient workflows across multiple departments, labs, and clinical teams. Manual coordination was causing delays and potential safety issues.",
      solution: "EceptionistCX deployed CellCX with specialized workflows for cell therapy coordination. Implementation included patient flow management, lab coordination, chain of identity tracking, SLA monitoring, and integration with LIMS and clinical systems.",
      results: [
        "Patient flow efficiency increased by 40%",
        "Lab coordination time reduced by 35%",
        "Chain of custody compliance improved to 100%"
      ],
      goLiveDate: "February 2024 in 8 weeks"
    },
    'public-health-scheduling': {
      slug: 'public-health-scheduling',
      title: t("useCaseFinder.useCases.publicHealthScheduling.title"),
      client: t("useCaseFinder.useCases.publicHealthScheduling.client"),
      product: t("useCaseFinder.products.schedulingcx"),
      industry: t("useCaseFinder.industries.publicHealth"),
      outcome: t("useCaseFinder.useCases.publicHealthScheduling.outcome"),
      headline: "Digital Scheduling for Public Health — How Regional Health Authority reduced no-shows with SchedulingCX",
      summary: "Reduced no-shows by 35% while improving access to public health services across multiple locations.",
      region: "Canada",
      integrations: ["Cerner", "HL7", "FHIR", "Azure"],
      metrics: [
        { label: "No-Shows", value: "−35%" },
        { label: "Access Time", value: "−50%" },
        { label: "Capacity", value: "+25%" },
      ],
      keyMetrics: [
        { icon: Star, label: "Patient Satisfaction", value: "4.6/5" },
        { icon: Target, label: "Appointment Completion", value: "+28%" },
        { icon: Rocket, label: "Time‑to‑Value", value: "< 30 days" },
      ],
      timeline: [
        { t: "Week 0–1", d: "Discovery & design" },
        { t: "Week 2–4", d: "Build & integrations" },
        { t: "Week 5–6", d: "UAT & training" },
        { t: "Week 7", d: "Go‑live" },
      ],
      quotes: [
        { q: "SchedulingCX revolutionized our public health service delivery.", a: "Public Health Director, Regional Health Authority" },
        { q: "The automated reminders have been a game changer.", a: "Operations Manager, Regional Health Authority" },
      ],
      context: "Regional Health Authority struggled with high no-show rates and inefficient scheduling across multiple public health service locations. Manual scheduling processes were creating barriers to care access.",
      solution: "EceptionistCX deployed SchedulingCX with automated reminders, self-service scheduling, and capacity management. Implementation included multi-location scheduling, automated notifications, waitlist management, and integration with existing health systems.",
      results: [
        "No-show rates reduced by 35%",
        "Average access time decreased by 50%",
        "Service capacity increased by 25%"
      ],
      goLiveDate: "January 2024 in 5 weeks"
    },
    'probation-digital-referrals': {
      slug: 'probation-digital-referrals',
      title: t("useCaseFinder.useCases.probationDigitalReferrals.title"),
      client: t("useCaseFinder.useCases.probationDigitalReferrals.client"),
      product: t("useCaseFinder.products.probationcx"),
      industry: t("useCaseFinder.industries.corrections"),
      outcome: t("useCaseFinder.useCases.probationDigitalReferrals.outcome"),
      headline: "From Paper to Digital Referrals — How County Probation Department standardized ProbationCX workflows",
      summary: "Standardized referrals across 120+ programs while improving accountability and reducing processing time.",
      region: "US",
      integrations: ["SFTP", "SSO", "Azure"],
      metrics: [
        { label: "Processing Time", value: "−65%" },
        { label: "Accuracy", value: "+45%" },
        { label: "Compliance", value: "+30%" },
      ],
      keyMetrics: [
        { icon: Star, label: "Officer Satisfaction", value: "4.7/5" },
        { icon: Target, label: "Referral Completion", value: "+55%" },
        { icon: Rocket, label: "Time‑to‑Value", value: "< 40 days" },
      ],
      timeline: [
        { t: "Week 0–2", d: "Discovery & design" },
        { t: "Week 3–5", d: "Build & integrations" },
        { t: "Week 6–7", d: "UAT & training" },
        { t: "Week 8", d: "Go‑live" },
      ],
      quotes: [
        { q: "ProbationCX eliminated our paper-based referral nightmare.", a: "Probation Supervisor, County Probation Department" },
        { q: "The digital workflow has improved our accountability significantly.", a: "Program Coordinator, County Probation Department" },
      ],
      context: "County Probation Department relied on paper-based referral processes across 120+ programs, leading to delays, errors, and lack of accountability. Officers spent significant time on administrative tasks.",
      solution: "EceptionistCX deployed ProbationCX with digital referral workflows, standardized forms, and secure information exchange. Implementation included role-based access, automated notifications, audit trails, and integration with existing justice systems.",
      results: [
        "Referral processing time reduced by 65%",
        "Data accuracy improved by 45%",
        "Compliance reporting increased by 30%"
      ],
      goLiveDate: "December 2023 in 6 weeks"
    },
    'retail-vax-waitlists': {
      slug: 'retail-vax-waitlists',
      title: t("useCaseFinder.useCases.retailVaxWaitlists.title"),
      client: t("useCaseFinder.useCases.retailVaxWaitlists.client"),
      product: t("useCaseFinder.products.vaxmanagercx"),
      industry: t("useCaseFinder.industries.retailCare"),
      outcome: t("useCaseFinder.useCases.retailVaxWaitlists.outcome"),
      headline: "Retail Vaccination with Smart Waitlists — How National Retail Health Network maximized VaxManagerCX efficiency",
      summary: "Filled 95% of missed slots via intelligent waitlists while improving customer experience and operational efficiency.",
      region: "US",
      integrations: ["Epic", "FHIR", "Azure", "SSO"],
      metrics: [
        { label: "Slot Utilization", value: "+95%" },
        { label: "Customer Satisfaction", value: "+40%" },
        { label: "Operational Efficiency", value: "+60%" },
      ],
      keyMetrics: [
        { icon: Star, label: "Customer Satisfaction", value: "4.8/5" },
        { icon: Target, label: "Vaccination Completion", value: "+48%" },
        { icon: Rocket, label: "Time‑to‑Value", value: "< 25 days" },
      ],
      timeline: [
        { t: "Week 0–1", d: "Discovery & design" },
        { t: "Week 2–3", d: "Build & integrations" },
        { t: "Week 4", d: "UAT & training" },
        { t: "Week 5", d: "Go‑live" },
      ],
      quotes: [
        { q: "VaxManagerCX transformed our vaccination operations.", a: "Operations Director, National Retail Health Network" },
        { q: "The waitlist automation is incredibly efficient.", a: "Site Manager, National Retail Health Network" },
      ],
      context: "National Retail Health Network struggled with missed vaccination appointments and inefficient waitlist management across multiple retail locations. Manual processes were creating operational inefficiencies.",
      solution: "EceptionistCX deployed VaxManagerCX with intelligent waitlist management, automated reminders, and self-service scheduling. Implementation included QR code badges, real-time dashboards, reminder systems, and integration with retail systems.",
      results: [
        "Missed slot utilization improved to 95%",
        "Customer satisfaction increased by 40%",
        "Operational efficiency improved by 60%"
      ],
      goLiveDate: "November 2023 in 4 weeks"
    },
    'econsult-access': {
      slug: 'econsult-access',
      title: t("useCaseFinder.useCases.econsultAccess.title"),
      client: t("useCaseFinder.useCases.econsultAccess.client"),
      product: t("useCaseFinder.products.econsultcx"),
      industry: t("useCaseFinder.industries.hospitals"),
      outcome: t("useCaseFinder.useCases.econsultAccess.outcome"),
      headline: "Faster Specialist Advice with eConsults — How Integrated Delivery Network accelerated eConsultCX workflows",
      summary: "Avoided 28% of in-person specialist visits while improving access to expert clinical guidance.",
      region: "US",
      integrations: ["Epic", "Cerner", "FHIR", "Teams"],
      metrics: [
        { label: "In-Person Visits", value: "−28%" },
        { label: "Response Time", value: "−70%" },
        { label: "Access", value: "+45%" },
      ],
      keyMetrics: [
        { icon: Star, label: "Provider Satisfaction", value: "4.9/5" },
        { icon: Target, label: "Consult Completion", value: "+65%" },
        { icon: Rocket, label: "Time‑to‑Value", value: "< 35 days" },
      ],
      timeline: [
        { t: "Week 0–2", d: "Discovery & design" },
        { t: "Week 3–6", d: "Build & integrations" },
        { t: "Week 7–8", d: "UAT & training" },
        { t: "Week 9", d: "Go‑live" },
      ],
      quotes: [
        { q: "eConsultCX has revolutionized our specialist access.", a: "Medical Director, Integrated Delivery Network" },
        { q: "The asynchronous workflow is incredibly efficient.", a: "Specialist Physician, Integrated Delivery Network" },
      ],
      context: "Integrated Delivery Network needed to improve access to specialist advice while reducing unnecessary in-person visits. Long wait times for specialist consultations were impacting patient care.",
      solution: "EceptionistCX deployed eConsultCX with asynchronous provider-to-provider consultations, smart routing, and flexible attachments. Implementation included status notifications, conversion to referrals, and integration with major EHRs.",
      results: [
        "In-person specialist visits reduced by 28%",
        "Average response time decreased by 70%",
        "Specialist access improved by 45%"
      ],
      goLiveDate: "October 2023 in 7 weeks"
    },
    'multi-agency-case-management': {
      slug: 'multi-agency-case-management',
      title: t("useCaseFinder.useCases.multiAgencyCaseManagement.title"),
      client: t("useCaseFinder.useCases.multiAgencyCaseManagement.client"),
      product: t("useCaseFinder.products.casecx"),
      industry: t("useCaseFinder.industries.publicHealth"),
      outcome: t("useCaseFinder.useCases.multiAgencyCaseManagement.outcome"),
      headline: "Multi-Agency Case Coordination — How State Health & Human Services unified CaseCX workflows",
      summary: "Created single view of client across 7 programs while improving service delivery and reducing duplication.",
      region: "US",
      integrations: ["Epic", "Cerner", "HL7", "FHIR"],
      metrics: [
        { label: "Service Coordination", value: "+50%" },
        { label: "Duplication", value: "−40%" },
        { label: "Outcomes", value: "+35%" },
      ],
      keyMetrics: [
        { icon: Star, label: "Case Manager Satisfaction", value: "4.8/5" },
        { icon: Target, label: "Case Completion", value: "+42%" },
        { icon: Rocket, label: "Time‑to‑Value", value: "< 50 days" },
      ],
      timeline: [
        { t: "Week 0–3", d: "Discovery & design" },
        { t: "Week 4–8", d: "Build & integrations" },
        { t: "Week 9–10", d: "UAT & training" },
        { t: "Week 11", d: "Go‑live" },
      ],
      quotes: [
        { q: "CaseCX provides the visibility we've always needed.", a: "Program Director, State Health & Human Services" },
        { q: "The multi-agency coordination is seamless.", a: "Case Manager, State Health & Human Services" },
      ],
      context: "State Health & Human Services managed complex cases across 7 different programs with limited coordination and visibility. Case managers struggled with fragmented information and duplicate services.",
      solution: "EceptionistCX deployed CaseCX with flexible workflow engine, custom forms, integrated notifications, and SLA tracking. Implementation included closed-loop referrals, scheduling integration, and care coordination workspace.",
      results: [
        "Service coordination improved by 50%",
        "Service duplication reduced by 40%",
        "Client outcomes improved by 35%"
      ],
      goLiveDate: "September 2023 in 8 weeks"
    },
    'consumer-self-service': {
      slug: 'consumer-self-service',
      title: t("useCaseFinder.useCases.consumerSelfService.title"),
      client: t("useCaseFinder.useCases.consumerSelfService.client"),
      product: t("useCaseFinder.products.consumercx"),
      industry: t("useCaseFinder.industries.hospitals"),
      outcome: t("useCaseFinder.useCases.consumerSelfService.outcome"),
      headline: "Self-Service Scheduling Portal — How Regional Health System empowered ConsumerCX engagement",
      summary: "Achieved 30% shift from phone to digital bookings while improving patient satisfaction and reducing staff workload.",
      region: "US",
      integrations: ["Epic", "Cerner", "FHIR", "SSO"],
      metrics: [
        { label: "Digital Adoption", value: "+30%" },
        { label: "Staff Efficiency", value: "+25%" },
        { label: "Patient Satisfaction", value: "+35%" },
      ],
      keyMetrics: [
        { icon: Star, label: "Patient Satisfaction", value: "4.7/5" },
        { icon: Target, label: "Self-Service Usage", value: "+65%" },
        { icon: Rocket, label: "Time‑to‑Value", value: "< 30 days" },
      ],
      timeline: [
        { t: "Week 0–2", d: "Discovery & design" },
        { t: "Week 3–5", d: "Build & integrations" },
        { t: "Week 6–7", d: "UAT & training" },
        { t: "Week 8", d: "Go‑live" },
      ],
      quotes: [
        { q: "ConsumerCX has transformed our patient engagement.", a: "Patient Experience Director, Regional Health System" },
        { q: "The self-service portal is incredibly user-friendly.", a: "Patient Services Manager, Regional Health System" },
      ],
      context: "Regional Health System received high volumes of phone calls for appointment scheduling, creating bottlenecks and reducing staff efficiency. Patients wanted more convenient self-service options.",
      solution: "EceptionistCX deployed ConsumerCX with self-registration, self-scheduling, digital forms, and notifications. Implementation included mobile-friendly design, workflow participation, and integration with existing systems.",
      results: [
        "Digital booking adoption increased by 30%",
        "Staff efficiency improved by 25%",
        "Patient satisfaction increased by 35%"
      ],
      goLiveDate: "August 2023 in 6 weeks"
    },
    'dfci-cell-therapy': {
      slug: 'dfci-cell-therapy',
      title: t("useCaseFinder.useCases.dfciCellTherapy.title"),
      client: t("useCaseFinder.useCases.dfciCellTherapy.client"),
      product: t("useCaseFinder.products.schedulingcx"),
      industry: t("useCaseFinder.industries.research"),
      outcome: t("useCaseFinder.useCases.dfciCellTherapy.outcome"),
      headline: "Cell Therapy Lab Throughput Boost — How Dana-Farber Cancer Institute optimized SchedulingCX for complex manufacturing",
      summary: "Achieved 90% direct scheduling by clinical users while increasing capacity utilization across 100+ protocols.",
      region: "US",
      integrations: ["Epic", "HL7", "FHIR", "Azure"],
      metrics: [
        { label: "Direct Scheduling", value: "90%" },
        { label: "Capacity Utilization", value: "+35%" },
        { label: "Admin Time", value: "−50%" },
      ],
      keyMetrics: [
        { icon: Star, label: "Clinical Satisfaction", value: "4.9/5" },
        { icon: Target, label: "Protocol Efficiency", value: "+40%" },
        { icon: Rocket, label: "Time‑to‑Value", value: "< 5 months" },
      ],
      timeline: [
        { t: "Month 0–1", d: "Discovery & design" },
        { t: "Month 2–4", d: "Build & integrations" },
        { t: "Month 5", d: "UAT & training" },
        { t: "Month 5", d: "Go‑live Nov 2017" },
      ],
      quotes: [
        { q: "Eceptionist gave us the visibility and control we needed to run at higher capacity without compromising safety.", a: "Lab Leadership, Cell Manipulation Core Facility" },
        { q: "The self-service scheduling has transformed how our clinical teams coordinate complex protocols.", a: "Clinical Operations Manager, Dana-Farber" },
      ],
      context: "Dana-Farber Cancer Institute's Cell Therapy Lab faced significant challenges coordinating complex, multi-step manufacturing pipelines across 100+ protocols. Manual scheduling via email created bottlenecks and blind spots in lab capacity.",
      solution: "EceptionistCX deployed SchedulingCX + ReferralCX configured for cell therapy manufacturing with custom calendars, capacity controls, and automated infusion schedules. Implementation included linked multi-step procedures, capacity management, weekly calendar views, self-service requests, queues & workload views, and HL7 EMR integration.",
      results: [
        "90% of procedures scheduled directly by clinical users without lab manager intervention",
        "Higher capacity utilization by filling previously underutilized processing slots",
        "Increased volume & revenue from efficiency gains",
        "Faster response to patient availability via direct scheduling",
        "Reduced admin overhead—lab managers spend more time on lab work"
      ],
      goLiveDate: "November 2017 in 5 months"
    }
  }

  return caseStudies[slug] || caseStudies['oncology-referrals-referralcx']
}
